import {
  type CallHomeWidget,
  type CameraGroupWidgetSettings,
  CommonWidgetUtils,
  type HistogramRange,
  type IRankingResult,
  type NormalizedDataSets,
  type RankingWidgetSettings,
  type ReportWidgetSettings,
  type TickerWidgetSettings,
  WIDGET_TYPE_CAMERA_GROUP,
  WIDGET_TYPE_LOCATION_RANKING,
  WIDGET_TYPE_RANKING,
  WIDGET_TYPE_RANKING_THRESHOLD,
  WIDGET_TYPE_REPORT,
  WIDGET_TYPE_REPORT_THRESHOLD,
} from '@solinkcorp/vd-common';
import { type IEvent } from 'clients/PlatformApi';
import { type Immutable } from 'immer';

interface StoredWidgetCommon {
  fetched: boolean;
  fetching: boolean;
  lastFetchDataParams?: unknown; // HistogramWidgetFetchParams | TickerWidgetFetchParams
  error?: true;
}

export interface StoredReportWidget
  extends StoredWidgetCommon, CallHomeWidget<ReportWidgetSettings> {
  dataSets?: NormalizedDataSets[];
  dataSetTypes?: string[];
  isEmpty?: boolean;
  range?: HistogramRange;
}

export interface StoredRankingWidget
  extends StoredWidgetCommon, CallHomeWidget<RankingWidgetSettings> {
  dataSets?: IRankingResult[];
  isEmpty?: boolean;
}

export type StoredThresholdWidget = StoredReportWidget | StoredRankingWidget;

export interface StoredCameraGroupWidget
  extends StoredWidgetCommon, CallHomeWidget<CameraGroupWidgetSettings> {}

export interface StoredTickerWidget
  extends StoredWidgetCommon, CallHomeWidget<TickerWidgetSettings> {}

/**
 * This is a widget with some extra data attached to it, like data from platform API.
 */
export type StoredWidget =
  | StoredReportWidget
  | StoredRankingWidget
  | StoredCameraGroupWidget
  | StoredTickerWidget;

export function isStoredReportWidget(widget: StoredWidget): widget is StoredReportWidget {
  return CommonWidgetUtils.isReportWidget(widget);
}

export function isStoredRankingWidget(widget: StoredWidget): widget is StoredRankingWidget {
  return CommonWidgetUtils.isRankingWidget(widget);
}

export function isStoredCameraGroupWidget(widget: StoredWidget): widget is StoredCameraGroupWidget {
  return widget.widgetSettings.type === WIDGET_TYPE_CAMERA_GROUP;
}

export function isImmutableStoredReportWidget(
  widget: Immutable<StoredWidget>,
): widget is Immutable<StoredReportWidget> {
  const type = widget.widgetSettings.type;
  return type === WIDGET_TYPE_REPORT || type === WIDGET_TYPE_REPORT_THRESHOLD;
}

export function isImmutableStoredRankingWidget(
  widget: Immutable<StoredWidget>,
): widget is Immutable<StoredRankingWidget> {
  const type = widget.widgetSettings.type;
  return (
    type === WIDGET_TYPE_RANKING ||
    type === WIDGET_TYPE_LOCATION_RANKING ||
    type === WIDGET_TYPE_RANKING_THRESHOLD
  );
}

export interface TickerData {
  tickerTotal: number;
  tickerTotalPrev: number;
  tickerSummaryValue: number;
  tickerSummaryValueTotal: number;
  tickerSummaryValuePrev: number;
  tickerEvents: IEvent[];
  tickerPollingData: IEvent[];
  tickerPollingEndTime: Date | undefined;
  tickerConflictDict: { [eventId: string]: boolean };
}
